import React, { FC } from 'react';

import clsx from 'clsx';

import { FormattedRecipeProduct } from '@domains/recipes';

import { getFirstCategorySlug } from '@utils/product/route';
import { makeProductRoute } from '@utils/route';
import { applyTypograf } from '@utils/typograf';

import { FavoriteButton } from '@components/common/FavoriteButton/FavoriteButton';
import { Link } from '@components/common/Link/Link';
import { Picture } from '@components/common/Picture/Picture';
import { QuantityPicker } from '@components/common/QuantityPicker/QuantityPicker';
import { DeleteIcon } from '@components/icons';
import { ProductPricePerUnit } from '@components/product/ProductPricePerUnit/ProductPricePerUnit';
import { ProductPricePresenter } from '@components/product/ProductPricePresenter/ProductPricePresenter';

import styles from './RecipeBuyProductsDialogItem.module.scss';

export type RecipeBuyProductsDialogItemProps = {
    className?: string;
    item: FormattedRecipeProduct;
    onChange(value: number): void;
    onDelete(): void;
};

export const RecipeBuyProductsDialogItem: FC<RecipeBuyProductsDialogItemProps> = (props) => {
    const { className, item, onChange, onDelete } = props;

    const { product, initialQuantity, quantity } = item;
    const { name, measure_unit, purchase_unit, primary_image, id, slug, is_favorite, categories } =
        product;

    const fistCategorySlug = getFirstCategorySlug(categories);
    const productRoute = fistCategorySlug ? makeProductRoute(slug, fistCategorySlug) : null;

    const picture = (
        <Picture
            sources={primary_image}
            className={styles.image}
            classes={{ picture: styles.picture }}
        />
    );

    return (
        <div className={clsx(styles.root, className)}>
            <div className={styles.imageBlock}>
                {productRoute ? (
                    <Link route={productRoute} className={styles.imageLink}>
                        {picture}
                    </Link>
                ) : (
                    <div className={styles.imageLink}>{picture}</div>
                )}

                <FavoriteButton
                    type="product"
                    id={id}
                    initialIsFavorite={is_favorite}
                    className={styles.favoritesButton}
                    shouldReloadAfterLogin={false}
                    hasShadow
                />
            </div>

            <div className={styles.nameAndPricePerUnitBlock}>
                {productRoute ? (
                    <Link route={productRoute} className={styles.name}>
                        {applyTypograf(name)}
                    </Link>
                ) : (
                    <span className={styles.name}>{applyTypograf(name)}</span>
                )}
                <ProductPricePerUnit measureUnit={measure_unit} className={styles.pricePerUnit} />
            </div>
            <div className={styles.quantityAndPriceBlock}>
                <QuantityPicker
                    onDebounceQuantityChange={onChange}
                    initialQuantity={initialQuantity}
                    unit={measure_unit.type.name}
                    step={purchase_unit.measure_units}
                    className={styles.quantityPicker}
                />
                <ProductPricePresenter
                    purchaseUnit={purchase_unit}
                    quantity={quantity}
                    classes={{
                        root: styles.priceBlock,
                        price: styles.price,
                        promotionPrice: styles.promotionPrice,
                    }}
                />
            </div>
            <button type="button" className={styles.deleteButton} onClick={onDelete}>
                <DeleteIcon className={styles.deleteIcon} />
            </button>
        </div>
    );
};
